import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import ScoreRing from '../common/ScoreRing';
import { FiArrowRight, FiMessageSquare, FiUser, FiGitBranch, FiAward, FiZap, FiCheckSquare, FiFolder } from 'react-icons/fi';
import type { Task, Project } from '../../types/work';

// ── Types ──
interface PlatformEvent {
  id?: string;
  category: string;
  event_type: string;
  target_type?: string;
  target_id?: string;
  name?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface ResumeItem {
  type: 'session' | 'persona' | 'workflow' | 'gauntlet' | 'match' | 'task' | 'project';
  title: string;
  subtitle: string;
  accent: string;
  icon: React.ReactNode;
  path: string;
  personaId?: string;
  personaName?: string;
  scoreValue?: number;
  statusColor?: string;
}

// ── Accent border lookup (Tailwind purge-safe) ──
const BORDER_DARK: Record<string, string> = {
  purple: 'border-purple-600',
  blue: 'border-blue-600',
  amber: 'border-amber-600',
  emerald: 'border-emerald-600',
  cyan: 'border-cyan-600',
  indigo: 'border-indigo-600',
  violet: 'border-violet-600',
};
const BORDER_LIGHT: Record<string, string> = {
  purple: 'border-purple-500',
  blue: 'border-blue-500',
  amber: 'border-amber-500',
  emerald: 'border-emerald-500',
  cyan: 'border-cyan-500',
  indigo: 'border-indigo-500',
  violet: 'border-violet-500',
};
const ICON_BG_DARK: Record<string, string> = {
  purple: 'bg-purple-900/30',
  blue: 'bg-blue-900/30',
  amber: 'bg-amber-900/30',
  emerald: 'bg-emerald-900/30',
  cyan: 'bg-cyan-900/30',
  indigo: 'bg-indigo-900/30',
  violet: 'bg-violet-900/30',
};
const ICON_BG_LIGHT: Record<string, string> = {
  purple: 'bg-purple-50',
  blue: 'bg-blue-50',
  amber: 'bg-amber-50',
  emerald: 'bg-emerald-50',
  cyan: 'bg-cyan-50',
  indigo: 'bg-indigo-50',
  violet: 'bg-violet-50',
};

// ── Pure function: extract resume items from events ──
export function extractResumeItems(
  events: PlatformEvent[],
  boardItems?: { tasks: Task[]; projects: Project[] },
  maxItems: number = 5,
): ResumeItem[] {
  const seen = new Set<string>();
  const items: ResumeItem[] = [];

  // Prepend Board items (most recently updated in-progress)
  if (boardItems) {
    const inProgressTasks = boardItems.tasks
      .filter(t => t.status === 'in_progress' || t.status === 'todo')
      .sort((a, b) => (b.updated_at || b.created_at).localeCompare(a.updated_at || a.created_at))
      .slice(0, 2);

    for (const t of inProgressTasks) {
      items.push({
        type: 'task',
        title: t.title,
        subtitle: `${t.status.replace('_', ' ')} \u00b7 ${t.priority}`,
        accent: 'violet',
        icon: <FiCheckSquare className="w-4 h-4" />,
        path: '/tasks',
      });
      seen.add(`task-${t.id}`);
    }

    const activeProjects = boardItems.projects
      .filter(p => p.status === 'active')
      .sort((a, b) => (b.updated_at || b.created_at).localeCompare(a.updated_at || a.created_at))
      .slice(0, 1);

    for (const p of activeProjects) {
      items.push({
        type: 'project',
        title: p.title,
        subtitle: p.status.replace('_', ' '),
        accent: 'blue',
        icon: <FiFolder className="w-4 h-4" />,
        path: '/projects',
      });
      seen.add(`project-${p.id}`);
    }
  }

  const sorted = [...events].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  for (const evt of sorted) {
    if (items.length >= maxItems) break;
    const dedup = `${evt.category}-${evt.target_id || evt.event_type}`;
    if (seen.has(dedup)) continue;
    seen.add(dedup);

    const meta = evt.metadata || {};

    if (evt.category === 'persona' && evt.target_id) {
      items.push({
        type: 'persona',
        title: (evt.name || meta.persona_name || 'Persona').replace(/_/g, ' '),
        subtitle: evt.event_type.replace(/^persona\./, '').replace(/_/g, ' '),
        accent: 'purple',
        icon: <FiUser className="w-3.5 h-3.5" />,
        path: `/personas/forge?personaId=${evt.target_id}`,
        personaId: evt.target_id,
        personaName: evt.name || meta.persona_name,
      });
    } else if (evt.category === 'workflow' && evt.target_id) {
      items.push({
        type: 'workflow',
        title: (meta.workflow_name || 'Workflow').replace(/_/g, ' '),
        subtitle: meta.status || evt.event_type.replace(/^workflow\./, '').replace(/_/g, ' '),
        accent: 'blue',
        icon: <FiGitBranch className="w-3.5 h-3.5" />,
        path: `/workflows/${evt.target_id}`,
        statusColor:
          meta.status === 'completed'
            ? 'text-green-400'
            : meta.status === 'failed'
              ? 'text-red-400'
              : 'text-blue-400',
      });
    } else if (evt.event_type?.startsWith('gymnasium.match')) {
      items.push({
        type: 'match',
        title: `${(meta.persona_a || '?').replace(/_/g, ' ')} vs ${(meta.persona_b || '?').replace(/_/g, ' ')}`,
        subtitle: meta.winner_name ? `${meta.winner_name.replace(/_/g, ' ')} won` : 'Arena match',
        accent: 'amber',
        icon: <FiZap className="w-3.5 h-3.5" />,
        path: '/gymnasium',
      });
    } else if (evt.event_type?.startsWith('gymnasium.gauntlet')) {
      items.push({
        type: 'gauntlet',
        title: (meta.gauntlet_name || 'Gauntlet').replace(/_/g, ' '),
        subtitle:
          meta.pass_rate != null ? `${Math.round(meta.pass_rate * 100)}% pass rate` : 'Gauntlet run',
        accent: 'amber',
        icon: <FiAward className="w-3.5 h-3.5" />,
        path: meta.gauntlet_id ? `/gymnasium/gauntlets/${meta.gauntlet_id}` : '/gymnasium/gauntlets',
        scoreValue: typeof meta.pass_rate === 'number' ? meta.pass_rate : undefined,
      });
    } else if (evt.category === 'session' || evt.event_type?.startsWith('session.')) {
      items.push({
        type: 'session',
        title: (meta.persona_name || 'Session').replace(/_/g, ' '),
        subtitle: meta.first_message ? meta.first_message.slice(0, 60) : 'Chat session',
        accent: 'emerald',
        icon: <FiMessageSquare className="w-3.5 h-3.5" />,
        path: evt.target_id ? `/sessions/${evt.target_id}` : '/sessions',
        personaId: meta.persona_id,
        personaName: meta.persona_name,
      });
    }
  }

  return items;
}

// ── Component ──
interface ResumeCardsProps {
  events: PlatformEvent[];
  boardItems?: { tasks: Task[]; projects: Project[] };
  limit?: number;
}

const ResumeCards: React.FC<ResumeCardsProps> = ({ events, boardItems, limit }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const items = extractResumeItems(events, boardItems, limit ?? 5);

  if (items.length === 0) {
    return (
      <div
        className={`rounded-lg shadow-lg border-l-4 p-8 text-center ${
          darkMode ? 'bg-gray-800 border-indigo-600' : 'bg-white border-indigo-500'
        }`}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gradient-to-br from-indigo-500/20 to-purple-500/20">
          <FiUser className="w-7 h-7 text-indigo-400" />
        </div>
        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Welcome to the Command Center
        </h3>
        <p className={`text-sm max-w-md mx-auto ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Start by forging a persona — your recent activity will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <motion.div
          key={`${item.type}-${i}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08, duration: 0.3, ease: 'easeOut' }}
        >
        <div
          onClick={() => navigate(item.path)}
          className={`rounded-lg shadow-lg border-l-4 p-3 cursor-pointer transition-all duration-200 hover:translate-y-[-1px] ${
            darkMode
              ? `bg-gray-800 ${BORDER_DARK[item.accent] || 'border-gray-600'} hover:shadow-lg`
              : `bg-white ${BORDER_LIGHT[item.accent] || 'border-gray-300'} shadow-sm hover:shadow-md`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-1.5 rounded-lg shrink-0 ${
                darkMode
                  ? ICON_BG_DARK[item.accent] || 'bg-gray-700'
                  : ICON_BG_LIGHT[item.accent] || 'bg-gray-100'
              }`}
            >
              {item.icon}
            </div>
            {item.personaId && item.personaName && (
              <PersonaBadge
                personaId={item.personaId}
                personaName={item.personaName}
                size="xs"
                clickable={false}
              />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {item.title}
              </p>
              <p
                className={`text-xs truncate ${
                  item.statusColor || (darkMode ? 'text-gray-500' : 'text-gray-400')
                }`}
              >
                {item.subtitle}
              </p>
            </div>
            {item.scoreValue != null && <ScoreRing value={item.scoreValue} size="sm" showLabel={false} />}
            <FiArrowRight className={`w-3.5 h-3.5 shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          </div>
        </div>
        </motion.div>
      ))}
    </div>
  );
};

export default ResumeCards;
